"use client";

import { useTranslations } from "@i18n/react";
import { useEffect, useState } from "react";

import { cropImageToBlob } from "../utils/image-uploads/cropImageToBlob";
import type { PresignedResult, SelectedImage } from "./useImageUpload";
import { useImageUpload } from "./useImageUpload";

type Point = { x: number; y: number };
type Area = { x: number; y: number; width: number; height: number };

type CropState = {
  imageSrc: string;
  crop: Point;
  zoom: number;
  pixelCrop: Area | null;
};

type CroppedImage = {
  blob: Blob;
  previewUrl: string;
  originalSrc: string;
  lastCropState: { crop: Point; zoom: number };
};

export type { SelectedImage, PresignedResult };

export const useCroppedImageUpload = ({
  getPresignedUrl,
  onUploadComplete,
}: {
  getPresignedUrl: () => Promise<PresignedResult>;
  onUploadComplete: (image: SelectedImage) => void;
}) => {
  const t = useTranslations();
  const [cropState, setCropState] = useState<CropState | null>(null);
  const [croppedImage, setCroppedImage] = useState<CroppedImage | null>(null);
  const [isCropping, setIsCropping] = useState(false);
  const [cropError, setCropError] = useState<string | null>(null);

  const {
    upload,
    isUploading,
    error: uploadError,
    clearError: clearUploadError,
  } = useImageUpload({ getPresignedUrl, onUploadComplete });

  useEffect(() => {
    return () => {
      if (croppedImage) {
        URL.revokeObjectURL(croppedImage.previewUrl);
        if (croppedImage.originalSrc.startsWith("blob:")) {
          URL.revokeObjectURL(croppedImage.originalSrc);
        }
      }
    };
  }, [croppedImage]);

  const startCrop = (
    src: string,
    lastCropState?: { crop: Point; zoom: number },
  ) => {
    setCropState({
      imageSrc: src,
      crop: lastCropState?.crop ?? { x: 0, y: 0 },
      zoom: lastCropState?.zoom ?? 1,
      pixelCrop: null,
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) startCrop(URL.createObjectURL(file));
  };

  const confirmCrop = async () => {
    if (!cropState?.pixelCrop) return;
    setIsCropping(true);
    setCropError(null);
    try {
      const blob = await cropImageToBlob(
        cropState.imageSrc,
        cropState.pixelCrop,
      );
      setCroppedImage({
        blob,
        previewUrl: URL.createObjectURL(blob),
        originalSrc: cropState.imageSrc,
        lastCropState: { crop: cropState.crop, zoom: cropState.zoom },
      });
      setCropState(null);
    } catch {
      setCropError(t("events_image_crop_error"));
    } finally {
      setIsCropping(false);
    }
  };

  const save = async (): Promise<SelectedImage | null> => {
    if (!croppedImage) return null;
    const image = await upload(croppedImage.blob, {
      originalSrc: croppedImage.originalSrc,
      lastCropState: croppedImage.lastCropState,
    });
    if (image) setCroppedImage(null);
    return image;
  };

  return {
    cropState,
    setCropState,
    croppedImage,
    clearCroppedImage: () => setCroppedImage(null),
    isUploading: isCropping || isUploading,
    error: cropError ?? uploadError,
    clearError: () => {
      setCropError(null);
      clearUploadError();
    },
    handleFileChange,
    confirmCrop,
    startCrop,
    save,
  };
};
